/* workflowGraph — pure synthesis for WorkflowPipeline: workflow doc
 * steps[] → positioned flow-graph nodes + raw edges + loop frames, laid
 * out TOP-TO-BOTTOM. START (name + inputs) → step nodes → END (output
 * keys). Branches fork into then/else lanes that rejoin; loops become
 * framed containers; on_error compensation hangs right of its step on a
 * dashed edge. Edge geometry lives in edgeRouting (dir 'v'); wording in
 * workflowText. No fetches, no React. */
import type {
    RawEdge
} from './journeyLayout';
import type {
    CapResolver,
    WFrame,
    WNode,
    WNodeKind,
    WorkflowGraph
} from './workflowGraphTypes';
import {
    cleanExpr,
    exprText,
    inputLines,
    inputSummary,
    mapLines,
    onErrorNote,
    outputLines,
    outputSummary
} from './workflowText';

const NODE_W = 230;
const NODE_H = 54;
const PILL_H = 50;
const DEC_W = 248;
const DEC_H = 56;
const COMP_W = 212;
const COMP_H = 50;
const COMP_GAP = 56;
const COMP_DY = 16;
const V_GAP = 44;
const LANE_GAP = 30;
const PAD_X = 26;
const PAD_TOP = 30;
const FR_L = 18;
const FR_R = 48;
const FR_T = 36;
const FR_B = 14;

export const START_KEY = '__start__';
export const END_KEY = '__wfend__';

type BBox = [number, number, number, number]; // x0, y0, x1, y1

interface ExitRef {
    from: string;
    label: string;
    kind?: string;
    tipBody?: string;
}

interface Placed {
    entry: string;
    exits: ExitRef[];
    bb: BBox;
}

interface Ctx {
    nodes: WNode[];
    raw: RawEdge[];
    frames: WFrame[];
    problems: string[];
    resolve: CapResolver;
}

function union(a: BBox, b: BBox): BBox {
    return [Math.min(a[0], b[0]), Math.min(a[1], b[1]),
        Math.max(a[2], b[2]), Math.max(a[3], b[3])];
}

function addNode(ctx: Ctx, key: string, kind: WNodeKind, x: number, y: number,
    w: number, h: number, title: string, sub: string, tipTitle: string,
    tipBody: string, marker?: 'stop' | 'skip'): BBox {
    ctx.nodes.push({
        key,
        kind,
        title,
        sub,
        x,
        y,
        w,
        h,
        tipTitle,
        tipBody,
        marker
    });
    return [x, y, x + w, y + h];
}

function addEdge(ctx: Ctx, from: string, to: string, label: string,
    kind?: string, tipTitle?: string, tipBody?: string): void {
    ctx.raw.push({
        from,
        to,
        label,
        kind,
        tipTitle: tipTitle || `${from} → ${to}`,
        tipBody: tipBody || 'Runs next.'
    });
}

/** call / transform / unknown — one node, plus a compensation offshoot. */
function layoutLeaf(step: any, id: string, key: string, cx: number, y: number,
    ctx: Ctx): Placed {
    const kind = step?.kind;
    let nodeKind: WNodeKind = 'unknown';
    let title = `unknown kind '${String(kind)}'`;
    let tipBody = 'This step kind is not part of the workflow contract.';
    if (kind === 'call') {
        const isWf = step.workflow != null;
        nodeKind = isWf ? 'subflow' : 'call';
        if (isWf) {
            title = `run workflow ${String(step.workflow)}`;
        } else {
            const capId = String(step.capability ?? '?');
            const cap = ctx.resolve(capId);
            title = cap ? `${cap.capName} · ${cap.sourceName}` : capId;
        }
        tipBody = step.with != null
            ? mapLines(step.with).join('\n') : 'No arguments.';
        const note = onErrorNote(step.on_error);
        if (note) tipBody += `\n${note}`;
    } else if (kind === 'transform') {
        nodeKind = 'transform';
        title = 'shapes data';
        const v = step.value;
        tipBody = (v != null && typeof v === 'object' && !Array.isArray(v)
            && typeof v.$ !== 'string')
            ? mapLines(v).join('\n') : `${id} ← ${exprText(v)}`;
    } else {
        ctx.problems.push(`step ${id}: unknown kind "${String(kind)}"`);
    }
    const marker = step?.on_error === 'stop' ? 'stop'
        : step?.on_error === 'continue' ? 'skip' : undefined;
    let bb = addNode(ctx, key, nodeKind, cx - NODE_W / 2, y, NODE_W, NODE_H,
        title, `${id} · ${nodeKind === 'subflow' ? 'workflow call' : kind}`,
        `${id} — ${title}`, tipBody, marker);
    const oe = step?.on_error;
    if (oe != null && typeof oe === 'object' && oe.run) {
        const run = String(oe.run);
        const ck = `comp:${key}`;
        const withTxt = oe.with != null ? `${mapLines(oe.with).join('\n')}\n` : '';
        const cb = addNode(ctx, ck, 'comp', cx + NODE_W / 2 + COMP_GAP,
            y + COMP_DY, COMP_W, COMP_H, run, 'compensation · on failure',
            `compensation — ${run}`,
            `${withTxt}Runs only when ${id} fails; the workflow then stops.`);
        addEdge(ctx, key, ck, '', 'comp', `${id} → ${run}`,
            `Dashed: taken only when ${id} fails.`);
        bb = union(bb, cb);
    }
    return {
        entry: key,
        exits: [{
            from: key,
            label: ''
        }],
        bb
    };
}

function layoutBranch(step: any, id: string, key: string, cx: number,
    y: number, path: string, ctx: Ctx): Placed {
    const whenFull = cleanExpr(step.when);
    let bb = addNode(ctx, key, 'decision', cx - DEC_W / 2, y, DEC_W, DEC_H,
        `${cleanExpr(step.when, 56)}?`, `${id} · branch`, `${id} · branch`,
        `Takes THEN when ${whenFull} is true, ELSE otherwise.`);
    const laneY = y + DEC_H + V_GAP;
    const exits: ExitRef[] = [];
    const lane = (steps: any, side: 'then' | 'else') => {
        const tip = side === 'then'
            ? `Taken when ${whenFull}.` : `Taken when not ${whenFull}.`;
        const list = Array.isArray(steps) ? steps : [];
        if (!list.length) {
            exits.push({
                from: key,
                label: side,
                kind: side,
                tipBody: tip
            });
            return;
        }
        const mark = ctx.nodes.length;
        const p = layoutSeq(list, 0, laneY, `${path}.${side}`, ctx);
        if (!p) return;
        const dx = side === 'then'
            ? cx - LANE_GAP - p.bb[2] : cx + LANE_GAP - p.bb[0];
        for (let i = mark; i < ctx.nodes.length; i += 1) ctx.nodes[i].x += dx;
        addEdge(ctx, key, p.entry, side, side, `${id} → ${side}`, tip);
        exits.push(...p.exits);
        bb = union(bb, [p.bb[0] + dx, p.bb[1], p.bb[2] + dx, p.bb[3]]);
    };
    lane(step.then, 'then');
    lane(step.else, 'else');
    return {
        entry: key,
        exits,
        bb
    };
}

function layoutLoop(step: any, id: string, key: string, cx: number, y: number,
    path: string, ctx: Ctx): Placed {
    const label = `For each ${cleanExpr(step.for_each, 42)}`
        + (step.as ? ` as ${step.as}` : '');
    const body = Array.isArray(step.steps) ? step.steps : [];
    const mark = ctx.nodes.length;
    const p = body.length ? layoutSeq(body, cx, y + FR_T, path, ctx) : null;
    if (!p) {
        ctx.problems.push(`loop ${id}: no steps to draw inside the frame`);
        const bb = addNode(ctx, key, 'unknown', cx - NODE_W / 2, y, NODE_W,
            NODE_H, label, `${id} · loop (empty)`, `${id} · loop`,
            'This loop has no body steps.');
        return {
            entry: key,
            exits: [{
                from: key,
                label: ''
            }],
            bb
        };
    }
    ctx.frames.push({
        key: `frame:${key}`,
        label,
        tipTitle: `${id} · loop`,
        tipBody: 'Repeats the framed steps once per item of '
            + `${cleanExpr(step.for_each)}`
            + (step.as ? `, each visible as ${step.as}` : '') + '.',
        nodeKeys: ctx.nodes.slice(mark).map((n) => n.key)
    });
    if (p.exits.length === 1) {
        addEdge(ctx, p.exits[0].from, p.entry, 'repeat', 'looprep',
            `${id} · next item`, 'Loops back while items remain.');
    }
    return {
        entry: p.entry,
        exits: p.exits,
        bb: [p.bb[0] - FR_L, y, p.bb[2] + FR_R, p.bb[3] + FR_B]
    };
}

function layoutSeq(steps: any[], cx: number, y0: number, path: string,
    ctx: Ctx): Placed | null {
    let entry: string | null = null;
    let prev: ExitRef[] = [];
    let bb: BBox | null = null;
    let y = y0;
    steps.forEach((step, i) => {
        const id = String(step?.id ?? `#${i + 1}`);
        const p = path ? `${path}.${id}` : id;
        const key = `s:${p}`;
        const k = step?.kind;
        let placed: Placed;
        if (k === 'branch') {
            placed = layoutBranch(step, id, key, cx, y, p, ctx);
        } else if (k === 'loop') {
            placed = layoutLoop(step, id, key, cx, y, p, ctx);
        } else {
            placed = layoutLeaf(step, id, key, cx, y, ctx);
        }
        if (!entry) entry = placed.entry;
        for (const ex of prev) {
            addEdge(ctx, ex.from, placed.entry, ex.label, ex.kind,
                undefined, ex.tipBody);
        }
        prev = placed.exits;
        bb = bb ? union(bb, placed.bb) : placed.bb;
        y = placed.bb[3] + V_GAP;
    });
    if (!entry || !bb) return null;
    return {
        entry,
        exits: prev,
        bb
    };
}

/** Frame rectangle from the CURRENT node positions (drag-aware). */
export function frameRect(frame: WFrame, byKey: Map<string, WNode>): {
    x: number;
    y: number;
    w: number;
    h: number;
} | null {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const k of frame.nodeKeys) {
        const n = byKey.get(k);
        if (!n) continue;
        x0 = Math.min(x0, n.x);
        y0 = Math.min(y0, n.y);
        x1 = Math.max(x1, n.x + n.w);
        y1 = Math.max(y1, n.y + n.h);
    }
    if (x0 === Infinity) return null;
    return {
        x: x0 - FR_L,
        y: y0 - FR_T,
        w: x1 - x0 + FR_L + FR_R,
        h: y1 - y0 + FR_T + FR_B
    };
}

export function buildWorkflowGraph(doc: any, resolve: CapResolver):
    WorkflowGraph {
    const ctx: Ctx = {
        nodes: [],
        raw: [],
        frames: [],
        problems: [],
        resolve
    };
    addNode(ctx, START_KEY, 'start', -NODE_W / 2, PAD_TOP, NODE_W, PILL_H,
        String(doc?.name || doc?.id || 'workflow'), inputSummary(doc?.input),
        `workflow ${String(doc?.id || '')}`.trim(), inputLines(doc?.input));
    const steps = Array.isArray(doc?.steps) ? doc.steps : [];
    const seq = layoutSeq(steps, 0, PAD_TOP + PILL_H + V_GAP, '', ctx);
    let endY = PAD_TOP + PILL_H + V_GAP;
    let tail: ExitRef[] = [{
        from: START_KEY,
        label: ''
    }];
    if (seq) {
        addEdge(ctx, START_KEY, seq.entry, '');
        endY = seq.bb[3] + V_GAP;
        tail = seq.exits;
    }
    addNode(ctx, END_KEY, 'end', -NODE_W / 2, endY, NODE_W, PILL_H, 'END',
        outputSummary(doc?.output), 'output', outputLines(doc?.output));
    for (const ex of tail) {
        addEdge(ctx, ex.from, END_KEY, ex.label, ex.kind, undefined,
            ex.tipBody);
    }
    // Normalize: keep every node AND frame inside the left padding.
    const byKey = new Map(ctx.nodes.map((n) => [n.key, n]));
    let minX = Math.min(...ctx.nodes.map((n) => n.x));
    for (const f of ctx.frames) {
        const r = frameRect(f, byKey);
        if (r) minX = Math.min(minX, r.x);
    }
    const dx = PAD_X - minX;
    for (const n of ctx.nodes) n.x += dx;
    return {
        nodes: ctx.nodes,
        raw: ctx.raw,
        frames: ctx.frames,
        problems: ctx.problems
    };
}
